/* ----- What a chat looked like when it was last left, so opening it again shows its
   messages straight away - then brought up to date in the background (new messages from its
   newest seq, changes from its change cursor) as a chat that's been open all along is. Only
   in memory, only a few chats, and only for the same login: never kept in
   the browser's storage, never shown to another user or in another company, never kept for a
   chat I've been taken out of. The same rules as the mobile app's (care_diary_app_rn
   lib/chatThreadCache.ts; change one, change both), kept apart from the store
   (chatThreadSnapshots) so they can be tested on their own ----- */

// Starting points, to tune once memory use is measured
export const THREAD_CACHE = {
  // Chats kept - the one left longest ago goes first (least recently used)
  maxChats: 10,
  // Messages kept of each chat, between the newest and those around where I was reading
  maxMessages: 60,
  // Of those, the newest
  recentMessages: 30,
  // All snapshots together, by an estimate of their size (estimateSnapshotBytes) - the one left
  // longest ago goes first beyond it. An estimate from what's in them, not a measured heap size
  budgetBytes: 3 * 1024 * 1024
};

/* ----- Roughly how much a snapshot holds, from what's in it (text and previews, as the
   characters of its JSON, at two bytes each) - for keeping all of them within a budget. Not a
   measurement: the JavaScript heap holds objects, not JSON ----- */
export const estimateSnapshotBytes = (snapshot) =>
  2 *
  (JSON.stringify(snapshot.messages ?? []).length +
    JSON.stringify(snapshot.members ?? []).length +
    JSON.stringify(snapshot.hiddenIds ?? []).length +
    JSON.stringify(snapshot.pinned ?? []).length);

/* ----- The snapshot of a chat being left, or null when it shouldn't be kept: nothing loaded,
   no known login, or a delete, delete for me, reaction or pin still on its way to the server (what's on screen
   then may not be what the server ends up with, and catching up wouldn't put it right).
   messages: server-confirmed only (unsent ones live in the outbox), oldest first.
   anchor: where I was reading ({ key, seq, offset }), null at the bottom. pinned (Web only):
   the chat's pinned messages, so the pinned bar shows straight away too.
   It keeps the newest messages, and - left reading further back - a few around that place,
   as two windows with the gap between them recorded ({ afterSeq, beforeSeq }; one window when
   they touch). The open chat may itself have a gap (opened from a snapshot, not yet filled):
   a window never reaches across it ----- */
export const takeThreadSnapshot = (state, now, limits = THREAD_CACHE) => {
  if (!state.owner || state.pendingChanges > 0) return null;
  const hiddenIds = [...state.hiddenIds];
  const hidden = new Set(hiddenIds);
  const shown = (m) => m.seq > state.floorSeq && !hidden.has(m.id);
  const visible = state.messages.filter(shown);
  if (!visible.length && !state.loadedUpTo) return null;
  // The open chat's own gap splits it in two: older side (up to afterSeq) and newer side
  const split = state.gap ? visible.findIndex((m) => m.seq >= state.gap.beforeSeq) : 0;
  const newerFrom = split < 0 ? visible.length : split;
  const sideStart = (index) => (state.gap && index < newerFrom ? 0 : newerFrom);
  const sideEnd = (index) => (state.gap && index < newerFrom ? newerFrom : visible.length);

  // The newest messages, from the newer side
  const recentFrom = Math.max(newerFrom, visible.length - limits.recentMessages);
  let windows = [[recentFrom, visible.length]];
  const anchorSeq = state.anchor?.seq ?? null;
  if (anchorSeq !== null && visible.length) {
    let at = visible.findIndex((m) => m.seq >= anchorSeq);
    if (at < 0) at = visible.length - 1;
    if (at < recentFrom) {
      // Around the place, within its side, with what's left of the allowance
      const room = limits.maxMessages - (visible.length - recentFrom);
      const lo = sideStart(at);
      const hi = sideEnd(at);
      let from = Math.max(lo, at - Math.floor(room / 2));
      const to = Math.min(hi, from + room);
      from = Math.max(lo, to - room);
      // Touching the newest (same side of the open chat's gap, no messages between): one
      // window. Not when the place is on the older side - even with nothing shown on the newer
      // side (all deleted for me), the gap still lies between
      const sameSide = !state.gap || at >= newerFrom;
      if (to >= recentFrom && sameSide) windows = [[from, visible.length]];
      else windows = [[from, to], ...windows];
    }
  }
  const messages = windows.flatMap(([from, to]) => visible.slice(from, to));
  // The open chat had a gap and nothing of it is left to show (all hidden from me): nothing kept -
  // opened again, it loads as usual rather than from an empty window that can't page anywhere
  if (state.gap && !messages.length) return null;
  const olderWindow = windows.length === 2 && windows[0][1] > windows[0][0];
  let gap = null;
  if (olderWindow && windows[1][1] > windows[1][0]) {
    gap = { afterSeq: visible[windows[0][1] - 1].seq, beforeSeq: visible[windows[1][0]].seq };
  } else if (olderWindow && state.gap) {
    // Every message on the open chat's newer side hidden from me: the gap still runs up to
    // where that side began - nothing between is ever taken as loaded
    gap = { afterSeq: visible[windows[0][1] - 1].seq, beforeSeq: state.gap.beforeSeq };
  }
  const kept = {
    owner: state.owner,
    conversationId: state.conversationId,
    savedAt: now,
    messages,
    gap,
    hasOlder: state.hasOlder || windows[0][0] > 0,
    // Where catching up starts: the newest seq known, however it arrived
    // The checkpoint as the open chat had it (chatThreadSync): everything up to it is here. Not
    // the newest message shown - one that came live past a hole would hide the hole for good
    loadedUpTo: state.loadedUpTo,
    changeCursor: state.changeCursor,
    floorSeq: state.floorSeq,
    hiddenIds,
    members: state.members,
    me: state.me,
    // Read up to here when it was left
    readSeq: state.readSeq,
    anchor: state.anchor ?? null,
    pinned: (state.pinned ?? []).filter(shown),
    bytes: 0
  };
  return { ...kept, bytes: estimateSnapshotBytes(kept) };
};

/* ----- Anything cleared or deleted for me is out of a snapshot before it's shown, whatever
   was saved - the same snapshot back when nothing was ----- */
const withoutHidden = (snapshot) => {
  const hidden = new Set(snapshot.hiddenIds);
  const shown = (m) => m.seq > snapshot.floorSeq && !hidden.has(m.id);
  const messages = snapshot.messages.filter(shown);
  const pinned = (snapshot.pinned ?? []).filter(shown);
  if (messages.length === snapshot.messages.length && pinned.length === (snapshot.pinned ?? []).length) {
    return snapshot;
  }
  // Cleared past the older window: no gap left to fill
  const gap = snapshot.gap && messages.some((m) => m.seq <= snapshot.gap.afterSeq) ? snapshot.gap : null;
  return { ...snapshot, messages, pinned, gap: gap ?? null };
};

/* ----- The snapshot to open a chat with, or null to load it as if for the first time.
   Being kept isn't proof of still being allowed in: it's only used while the chat is still in
   my current chat list (taken out, it leaves the list - and the snapshot is dropped then too),
   never for a group I only oversee (those reads go through the server, which records them),
   and never for anyone but the login that left it. How long ago doesn't matter while it's
   kept: whether it's shown at once or only after asking the server is snapshotTrusted's.
   listed: in my chat list as it is now - true / false, or undefined while it's loading ----- */
export const usableThreadSnapshot = (snapshot, { owner, listed, overseeing }) => {
  if (!snapshot || !owner || snapshot.owner !== owner) return null;
  if (listed !== true || overseeing) return null;
  return withoutHidden(snapshot);
};

/* ----- Deleted for me on another device (or tab) while the chat was kept: out of what's kept,
   and never back - its id joins the hidden ones, so a later copy is left out too ----- */
export const hideInSnapshot = (snapshot, messageId) => {
  if (!snapshot || snapshot.hiddenIds.includes(messageId)) return snapshot;
  const kept = withoutHidden({ ...snapshot, hiddenIds: [...snapshot.hiddenIds, messageId] });
  return { ...kept, bytes: estimateSnapshotBytes(kept) };
};

/* ----- Which kept snapshots go: any of another login, then - least recently used first - any
   beyond the number of chats or the size budget. One bigger than the whole budget on its
   own isn't kept at all (that chat loads as usual next time).
   kept: [{ key, owner, savedAt, bytes }] ----- */
export const snapshotsToDrop = (kept, { owner, limits = THREAD_CACHE }) => {
  const drop = kept.filter((s) => s.owner !== owner);
  const recentFirst = kept.filter((s) => !drop.includes(s)).sort((a, b) => b.savedAt - a.savedAt);
  let total = 0;
  recentFirst.forEach((s, index) => {
    total += s.bytes ?? 0;
    if (index >= limits.maxChats || total > limits.budgetBytes) drop.push(s);
  });
  return drop.map((s) => s.key);
};

/* ----- A page loaded into a gap: from its older side (afterSeq, newest-ward) or its newer side
   (beforeSeq, oldest-ward). The gap that's left, or null once the page reaches the other side
   or the server has no more there. from: "older" | "newer" ----- */
export const gapAfterPage = (gap, page, from) => {
  const seqs = page.data.map((m) => m.seq);
  if (from === "older") {
    if (!page.hasMore || !seqs.length || seqs.some((seq) => seq >= gap.beforeSeq)) return null;
    return { afterSeq: Math.max(...seqs), beforeSeq: gap.beforeSeq };
  }
  if (!page.hasMore || !seqs.length || seqs.some((seq) => seq <= gap.afterSeq)) return null;
  return { afterSeq: gap.afterSeq, beforeSeq: Math.min(...seqs) };
};

// Whether this seq is among the messages a gap hasn't loaded
export const inGap = (gap, seq) => !!gap && seq > gap.afterSeq && seq < gap.beforeSeq;

/* ----- Cleared up to clearedSeq: what's left of the gap and of older history. The gap stays
   only while something of its older side is left. Older history is still there to load when
   messages may lie between the clear and the oldest one left - always so when the clear landed
   inside the gap (everything after it up to the newer side is still to come). remaining: the
   messages left (after the clear), oldest first ----- */
export const afterClear = ({ remaining, gap, hasOlder }, clearedSeq) => {
  const keepGap = !!gap && remaining.some((m) => m.seq <= gap.afterSeq);
  const gapDropped = !!gap && !keepGap;
  const oldest = remaining.length ? remaining[0].seq : undefined;
  const more = oldest !== undefined && oldest > clearedSeq + 1 && (hasOlder || gapDropped);
  return { gap: keepGap ? gap : null, hasOlder: more };
};

/* ----- Where the gap's marker goes among the rows (oldest first; seq null for one still
   sending): before the first row of its newer side - or, every message there hidden from me
   (deleted for me), after the last message, so the gap can still be reached and filled. -1:
   no gap ----- */
export const gapMarkerIndex = (rowSeqs, gap) => {
  if (!gap) return -1;
  const at = rowSeqs.findIndex((seq) => seq !== null && seq >= gap.beforeSeq);
  if (at >= 0) return at;
  let last = -1;
  rowSeqs.forEach((seq, index) => {
    if (seq !== null) last = index;
  });
  return last + 1;
};

/* ----- Older history is asked for one page at a time, and not again after the start of the
   chat is reached or while a failed page waits for Retry ----- */
export const shouldLoadOlder = ({ hasOlder, inFlight, failed, loaded }) =>
  hasOlder && !inFlight && !failed && loaded > 0;

/* ----- Whether what's kept of a chat can be shown before the server has been asked again.
   Only while the connection is up (removals reach the tab only over it) and my chat list has
   been fetched since it last connected, since the tab last came back after a long time hidden,
   and since a vague "your chats changed" (no chat named) - a removal made meanwhile would have
   been missed. A list fetched while disconnected proves nothing. Otherwise the chat is asked
   for first (its details), and shown once the answer says it's still mine. The same as the
   mobile app's ----- */
export const snapshotTrusted = ({ listFetchedAt, resumedAt, connectedAt, untrustedAt, connected }) =>
  !!connected && listFetchedAt > 0 && listFetchedAt >= Math.max(resumedAt, connectedAt, untrustedAt);

/* ----- Asked first and the chat couldn't be reached: refused (403/404) - it's gone. Anything
   else (offline, a server error) leaves my access unknown, so what's kept isn't shown - the
   chat loads from the server as on a first open. Returns "lost" | "hide" ----- */
export const afterAccessCheckFailed = ({ status }) => (status === 403 || status === 404 ? "lost" : "hide");

/* ----- A change cursor the server sends back lower than the one asked from: its record of
   changes went back (a restore). What's kept can't be brought up to date from there - the
   chat loads afresh. Asked from a cursor it's already reached, it answers with the same one ----- */
export const changeCursorWentBack = (askedFrom, answered) => answered < askedFrom;

/* ----- Snapshots kept for a chat (or every chat) are dropped when I'm taken out of it, my
   access changes, or I log out. A chat still open at that moment then has nothing to keep
   when it's left: each drop moves a counter on, and a snapshot taken by a chat opened before
   the drop is thrown away rather than saved - so a late answer for a chat I've lost can't put
   it back ----- */
export const createSnapshotGuard = () => {
  let everything = 0;
  const perChat = new Map();
  const generation = (conversationId) => `${everything}:${perChat.get(conversationId) ?? 0}`;
  return {
    generation,
    dropChat: (conversationId) => perChat.set(conversationId, (perChat.get(conversationId) ?? 0) + 1),
    dropAll: () => {
      everything += 1;
    },
    // Taken while nothing was dropped since the chat opened
    canKeep: (conversationId, openedAt) => generation(conversationId) === openedAt,
  };
};
